import { gql } from '@apollo/client';
import React, { useEffect, useState } from 'react';

import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import {
  EntityIndicator,
  EntityIndicatorReputation,
  EntityReputationSource,
  getIndicatorSourceText,
} from '@sbu/views.entityindicator';

import { useLazyQuery } from 'shared/hooks';

const INDICATOR_QUERY = gql`
  query IndicatorQuery($entityId: ID!) {
    entity(id: $entityId) {
      id
      reputation
      reputationSources
    }
  }
`;

export interface IndicatorProps {
  entity?: {
    id?: string;
    reputation?: string | null;
    reputationSources?: EntityReputationSource[];
  };
  fetchData?: boolean;
}

export const Indicator = ({ entity, fetchData = false }: IndicatorProps) => {
  const [source, setSource] = useState<string | undefined>(undefined);
  const [entityData, setEntityData] = useState<
    | {
        reputation?: string | null;
        reputationSources?: EntityReputationSource[];
      }
    | undefined
  >(undefined);

  const showIndicatorSource = useFeatureFlag(
    DynamicFeatureFlag.threatIntelEntityEnrichment
  );

  const [getIndicatorData, { data, loading }] = useLazyQuery(INDICATOR_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'none',
    notifyErrors: false,
  });

  useEffect(() => {
    if (fetchData && !data && !loading && entity?.id) {
      getIndicatorData({ variables: { entityId: entity.id } });
    }
  }, [fetchData, data, loading, getIndicatorData, entity?.id]);

  useEffect(() => {
    if (
      fetchData &&
      data?.entity?.reputation &&
      data?.entity?.reputationSources &&
      !source
    ) {
      setSource(getIndicatorSourceText(data.entity.reputationSources));
      setEntityData(data.entity);
    }
  }, [data?.entity, fetchData, source]);

  useEffect(() => {
    if (
      !fetchData &&
      entity?.reputation &&
      entity?.reputationSources &&
      !source
    ) {
      setSource(getIndicatorSourceText(entity.reputationSources));
      setEntityData(entity);
    }
  }, [entity, fetchData, source]);

  return !entityData?.reputation ? null : (
    <EntityIndicator
      reputation={entityData.reputation as EntityIndicatorReputation}
      showTooltip
      source={showIndicatorSource ? source : undefined}
      variant='icon'
    />
  );
};
